/**
 * Which of the rail's dropdowns is open, and how the keyboard moves through them — ADR 9023,
 * as amended for Package C4 (brief part 1 §3: "si apre un'app alla volta").
 *
 * ONE app is open at a time, an accordion. The app holding the page the planner is on opens by
 * itself, on load and on every navigation into an app, and closes whichever was open; any other
 * app opens (closing the one that was) and closes when the planner says so. Opening an app never
 * navigates. The planner's last choice is kept in `localStorage` as a convenience only: a private
 * window, blocked storage or a malformed value give the default (only the current app open),
 * never a failure, so every access is inside `try`/`catch`.
 *
 * The state is still a set — of at most one member — so the rail reads `open.has(id)` whether
 * one or none is open.
 */

import { useCallback, useEffect, useState } from 'react';

import { FUTURE_GROUP, RAIL_APP_IDS, type RailGroupId } from './rail-layout';

export const RAIL_OPEN_STORAGE_KEY = 'bata.rail.openGroups';

const GROUP_IDS: readonly string[] = [...RAIL_APP_IDS, FUTURE_GROUP];

/**
 * The stored open group, as a list of at most one, or none. Never throws. A value written before
 * the accordion (several ids) gives its first valid id.
 */
export function readStoredOpenGroups(): readonly RailGroupId[] {
  try {
    const raw = globalThis.localStorage?.getItem(RAIL_OPEN_STORAGE_KEY);
    if (raw === null || raw === undefined) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is RailGroupId => typeof id === 'string' && GROUP_IDS.includes(id)).slice(0, 1);
  } catch {
    return [];
  }
}

function storeOpenGroups(open: ReadonlySet<RailGroupId>): void {
  try {
    globalThis.localStorage?.setItem(RAIL_OPEN_STORAGE_KEY, JSON.stringify([...open]));
  } catch {
    // A convenience, not state: the rail still works, it just forgets on reload.
  }
}

/** The open group (a set of at most one), and a toggle. `current` is the group holding the page, if any. */
export function useRailOpenGroups(
  current: RailGroupId | undefined,
): readonly [ReadonlySet<RailGroupId>, (id: RailGroupId) => void] {
  const [open, setOpen] = useState<ReadonlySet<RailGroupId>>(() => (
    new Set(current !== undefined ? [current] : readStoredOpenGroups())
  ));

  // Arriving in an app opens it, and closes the one that was open.
  useEffect(() => {
    if (current === undefined) return;
    setOpen((was) => (was.size === 1 && was.has(current) ? was : new Set([current])));
  }, [current]);

  const toggle = useCallback((id: RailGroupId) => {
    setOpen((was) => {
      const next: ReadonlySet<RailGroupId> = was.has(id) ? new Set() : new Set([id]);
      storeOpenGroups(next);
      return next;
    });
  }, []);

  return [open, toggle] as const;
}

/** The attribute that makes a control one stop of the rail's arrow-key walk. */
export const RAIL_ITEM = { 'data-rail-item': '' } as const;

/**
 * Up and Down move between the rail's controls, Home and End to the first and last. Enter and
 * Space are the buttons' own. Bound on a container; the stops are its `data-rail-item`
 * descendants in document order, so a closed group's entries — not rendered — are skipped.
 */
export function moveRailFocus(event: React.KeyboardEvent<HTMLElement>): void {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
  const items = [...event.currentTarget.querySelectorAll<HTMLElement>('[data-rail-item]')];
  const index = items.findIndex((item) => item === document.activeElement);
  if (index === -1 || items.length === 0) return;
  event.preventDefault();
  const last = items.length - 1;
  const next = event.key === 'Home' ? 0
    : event.key === 'End' ? last
      : event.key === 'ArrowDown' ? Math.min(index + 1, last)
        : Math.max(index - 1, 0);
  items[next]?.focus();
}
